import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { isAxiosError } from 'axios';
import { Loader2, X } from 'lucide-react';
import { useCreatePBCRequest, usePBCControlOptions } from '../../hooks/pbc/usePBCRequests.ts';
import type { PBCCreateBody } from '../../request/pbc/pbcRequests.ts';
import { useAuditYear } from '../../contexts/AuditYearContext.tsx';

type CreatePBCRequestModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Called when the create request comes back 403 (non-admin caller), so the
   * parent tracker page can disable write controls going forward. */
  onForbidden?: () => void;
};

/** "New PBC Request" create form (Feature 5.3, Controller report #29). Control
 * picker sourced from GET /api/sox-controls (up to 200 controls) — the PBC
 * request stores the control's raw UUID (`sox_control_id`), not its display
 * code, so the picker resolves that mapping for the user. */
export const CreatePBCRequestModal: React.FC<CreatePBCRequestModalProps> = ({ isOpen, onClose, onForbidden }) => {
  const [controlId, setControlId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const { selectedAuditYearId } = useAuditYear();
  const { data: controlOptions, isLoading: controlsLoading } = usePBCControlOptions();
  const createMutation = useCreatePBCRequest();

  useEffect(() => {
    if (!isOpen) return;
    setControlId('');
    setTitle('');
    setDescription('');
    setDueDate('');
    setFormError(null);
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleCreate = () => {
    if (!controlId) {
      setFormError('Select a control');
      return;
    }
    if (!title.trim()) {
      setFormError('Title is required');
      return;
    }
    setFormError(null);

    const body: PBCCreateBody = {
      sox_control_id: controlId,
      title: title.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(dueDate ? { due_date: dueDate } : {}),
      ...(selectedAuditYearId ? { audit_year_id: selectedAuditYearId } : {}),
    };

    createMutation.mutate(body, {
      onSuccess: () => onClose(),
      onError: (err) => {
        if (isAxiosError(err) && err.response?.status === 403) onForbidden?.();
      },
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        disabled={createMutation.isPending}
        aria-label="Close new PBC request popup"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="New PBC request"
        className="relative z-10 w-full max-w-lg rounded-xl border border-gray-200 bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">New PBC Request</h3>
            <p className="mt-1 text-sm text-gray-600">Request evidence from the client for a control.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={createMutation.isPending}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="pbc-control" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Control <span className="text-red-500">*</span>
            </label>
            <select
              id="pbc-control"
              value={controlId}
              onChange={(e) => setControlId(e.target.value)}
              disabled={createMutation.isPending || controlsLoading}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-gray-50"
            >
              <option value="">{controlsLoading ? 'Loading controls...' : 'Select a control'}</option>
              {(controlOptions ?? []).map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.control_id ? `${opt.control_id} — ${opt.title}` : opt.title || opt.id}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="pbc-title" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Title / PBC Type <span className="text-red-500">*</span>
            </label>
            <input
              id="pbc-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={createMutation.isPending}
              maxLength={500}
              placeholder="e.g. Q3 bank reconciliation support"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-gray-50"
            />
          </div>

          <div>
            <label htmlFor="pbc-due-date" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Due Date
            </label>
            <input
              id="pbc-due-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              disabled={createMutation.isPending}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-gray-50"
            />
          </div>

          <div>
            <label htmlFor="pbc-description" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Description
            </label>
            <textarea
              id="pbc-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={createMutation.isPending}
              rows={3}
              placeholder="Optional details for the client"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-gray-50"
            />
          </div>

          {formError ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{formError}</p>
          ) : null}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={createMutation.isPending}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={createMutation.isPending}
              className="inline-flex items-center gap-2 rounded-md border border-blue-600 bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Create Request
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreatePBCRequestModal;
